import React, { useEffect, useState } from 'react';
import { Modal, Tab, Loader, Dimmer, Message } from 'semantic-ui-react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../app/stores/store';
import UserStatsTab from './UserStatsTab';
import RateUserTab from './RateUserTab';
import ManageCommentsTab from './ManageCommentsTab';
import UserCommentsTab from './UserCommentsTab';

interface UserScoreModalProps {
    open: boolean;
    onClose: () => void;
    userId: string;
    sportId?: string;
}

export default observer(function UserScoreModal({ 
    open, 
    onClose, 
    userId, 
    sportId 
}: UserScoreModalProps) {
    const { t } = useTranslation();
    const { userRatingStore } = useStore();
    const { userRatingData, loading, loadUserRatings, clearUserRatingData } = userRatingStore;
    const [activeIndex, setActiveIndex] = useState(0);
    const [isMobile, setIsMobile] = useState(false);
    
    useEffect(() => {
        const checkIfMobile = () => {
            const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
            const isSmallScreen = window.innerWidth <= 768;
            setIsMobile(isTouchDevice && isSmallScreen);
        };

        checkIfMobile();
        window.addEventListener('resize', checkIfMobile);
        return () => window.removeEventListener('resize', checkIfMobile);
    }, []);

    useEffect(() => {
        if (open) {
            loadUserRatings(userId, sportId);
        }
        
        return () => {
            if (!open) {
                clearUserRatingData();
                setActiveIndex(0);
            }
        };
    }, [open, userId, sportId, loadUserRatings, clearUserRatingData]);

    const buildPanes = () => {
        const panes = [];

        // Stats tab - always visible
        panes.push({
            menuItem: { 
                key: 'stats', 
                icon: 'chart line', 
                content: isMobile ? '' : t('userRatings.stats')
            },
            render: () => (
                <Tab.Pane style={{ padding: isMobile ? '5px' : undefined }}>
                    <UserStatsTab 
                        userRatingData={userRatingData}
                        sportId={sportId}
                        isMobile={isMobile}
                    />
                </Tab.Pane>
            )
        });

        // Comments tab - visible if there are comments
        if (userRatingData?.comments && userRatingData.comments.length > 0) {
            const visibleCommentsCount = userRatingData.isOwner 
                ? userRatingData.comments.length 
                : userRatingData.comments.filter(c => c.isApproved).length;
            
            if (visibleCommentsCount > 0) {
                panes.push({
                    menuItem: { 
                        key: 'comments', 
                        icon: 'comments', 
                        content: isMobile 
                            ? `${visibleCommentsCount}` 
                            : t('userRatings.comments') + ` (${visibleCommentsCount})`
                    },
                    render: () => (
                        <Tab.Pane style={{ padding: isMobile ? '5px' : undefined }}>
                            <UserCommentsTab
                                comments={userRatingData.comments || []}
                                isOwner={userRatingData.isOwner}
                                isMobile={isMobile}
                                totalCount={userRatingData.totalCommentsCount || 0}
                                visibleCount={userRatingData.visibleCommentsCount || visibleCommentsCount}
                            />
                        </Tab.Pane>
                    )
                });
            }
        }

        // Rate user tab - only if canRate is true
        if (userRatingData?.canRate && userRatingData?.rateableGame) {
            panes.push({
                menuItem: { 
                    key: 'rate', 
                    icon: 'star', 
                    content: isMobile ? '' : t('userRatings.rateUser')
                },
                render: () => (
                    <Tab.Pane style={{ padding: isMobile ? '5px' : undefined }}>
                        <RateUserTab
                            userId={userId}
                            rateableGame={userRatingData.rateableGame!}
                            onSuccess={() => {
                                loadUserRatings(userId, sportId);
                                setActiveIndex(0);
                            }}
                            isMobile={isMobile}
                        />
                    </Tab.Pane>
                )
            });
        }

        // Manage comments tab - only if owner
        if (userRatingData?.isOwner) {
            const totalComments = userRatingData.comments ? userRatingData.comments.length : 0;
            panes.push({
                menuItem: { 
                    key: 'manage-comments', 
                    icon: 'settings', 
                    content: isMobile 
                        ? (totalComments > 0 ? `${totalComments}` : '')
                        : t('userRatings.manageComments') + (totalComments > 0 ? ` (${totalComments})` : '')
                },
                render: () => (
                    <Tab.Pane style={{ padding: isMobile ? '5px' : undefined }}>
                        <ManageCommentsTab
                            comments={userRatingData.comments || []}
                            isMobile={isMobile}
                            totalCount={userRatingData.totalCommentsCount || totalComments}
                        />
                    </Tab.Pane>
                )
            });
        }

        return panes;
    };

    return (
        <Modal 
            open={open} 
            onClose={onClose}
            size={isMobile ? 'fullscreen' : 'large'}
            closeIcon
            style={isMobile ? { margin: 0 } : undefined}
        >
            <Modal.Header style={{ fontSize: isMobile ? '1.1rem' : undefined }}>
                {t('userRatings.modalTitle')} - {userRatingData?.fullName}
            </Modal.Header>
            <Modal.Content 
                scrolling 
                style={{ 
                    minHeight: isMobile ? '70vh' : '500px',
                    padding: isMobile ? '10px' : undefined
                }}
            >
                {loading ? (
                    <Dimmer active inverted>
                        <Loader>{t('userRatings.loading')}</Loader>
                    </Dimmer>
                ) : userRatingData ? (
                    <Tab 
                        panes={buildPanes()} 
                        activeIndex={activeIndex}
                        onTabChange={(e, data) => setActiveIndex(data.activeIndex as number)}
                        menu={{ 
                            secondary: true, 
                            pointing: true,
                            ...(isMobile && { 
                                widths: buildPanes().length as any,
                                style: { fontSize: '0.85rem' }
                            })
                        }}
                    />
                ) : (
                    <Message warning>
                        <Message.Header>{t('userRatings.noData')}</Message.Header>
                        <p>{t('userRatings.noDataMessage')}</p>
                    </Message>
                )}
            </Modal.Content>
        </Modal>
    );
});